import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import Container from "../../common/Container";
import { Button } from "../../common/Button";
import { IconClose, IconMenu } from "../../common/Icons";
import { BrandLogo } from "../../common/BrandLogo";
import { useLockBodyScroll } from "../../common/hooks/useLockBodyScroll";
import { useScrolledPast } from "../../common/hooks/useScrollPosition";
import { useScrollSpy } from "../../common/hooks/useScrollSpy";
import { useSectionLink } from "../../common/utils/navigation";
import { SECTIONS } from "../../content/nav";
import {
  Actions,
  Bar,
  Brand,
  Burger,
  DrawerBackdrop,
  DrawerFooter,
  DrawerHead,
  DrawerItem,
  DrawerLabel,
  DrawerNav,
  DrawerPanel,
  HeaderSection,
  Nav,
  NavItem,
  SkipLink,
} from "./styles";

const SECTION_IDS: string[] = SECTIONS.map((s) => s.id);
/* Stable identity so the scrollspy effect doesn't re-subscribe every render. */
const NO_SECTIONS: string[] = [];

const Header = () => {
  const location = useLocation();
  const solid = useScrolledPast(30);
  const goToSection = useSectionLink();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const burgerRef = useRef<HTMLButtonElement>(null);
  const firstItemRef = useRef<HTMLButtonElement>(null);

  const onHome = location.pathname === "/";
  // Scrollspy only makes sense where the sections exist.
  const activeSection = useScrollSpy(onHome ? SECTION_IDS : NO_SECTIONS);

  useLockBodyScroll(drawerOpen);

  const closeDrawer = useCallback(() => setDrawerOpen(false), []);

  // A navigation while the drawer is open should dismiss it.
  useEffect(() => {
    closeDrawer();
  }, [location.pathname, closeDrawer]);

  useEffect(() => {
    if (!drawerOpen) return;

    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") closeDrawer();
    };

    document.addEventListener("keydown", onKey);
    const id = window.setTimeout(() => firstItemRef.current?.focus(), 220);

    return () => {
      document.removeEventListener("keydown", onKey);
      window.clearTimeout(id);
      // Send focus back to the control that opened the drawer.
      burgerRef.current?.focus();
    };
  }, [drawerOpen, closeDrawer]);

  /* Moves focus and scroll to <main>. Not an href — see SkipLink's styles. */
  const skipToContent = () => {
    const main = document.getElementById("main");
    if (!main) return;
    main.focus();
    main.scrollIntoView({ behavior: "auto", block: "start" });
  };

  const handleSection = (id: string) => () => {
    closeDrawer();
    goToSection(id)();
  };

  return (
    <>
      <SkipLink onClick={skipToContent}>Skip to content</SkipLink>

      <HeaderSection $solid={solid || !onHome || drawerOpen}>
        <Container>
          <Bar>
            <Brand to="/" aria-label="The Ghost — home">
              <BrandLogo height={26} alt="" />
            </Brand>

            <Nav aria-label="Primary">
              {SECTIONS.map((section) => (
                <NavItem
                  key={section.id}
                  onClick={handleSection(section.id)}
                  $active={onHome && activeSection === section.id}
                  aria-current={
                    onHome && activeSection === section.id ? "true" : undefined
                  }
                >
                  {section.label}
                </NavItem>
              ))}
            </Nav>

            <Actions>
              <Button variant="primary" onClick={handleSection("download")}>
                Play free
              </Button>
            </Actions>

            <Burger
              ref={burgerRef}
              onClick={() => setDrawerOpen(true)}
              aria-label="Open menu"
              aria-expanded={drawerOpen}
              aria-controls="mobile-drawer"
            >
              <IconMenu size={20} />
            </Burger>
          </Bar>
        </Container>
      </HeaderSection>

      <DrawerBackdrop $open={drawerOpen} onClick={closeDrawer} aria-hidden="true" />

      <DrawerPanel
        id="mobile-drawer"
        $open={drawerOpen}
        role="dialog"
        aria-modal={drawerOpen}
        aria-label="Menu"
        aria-hidden={!drawerOpen}
      >
        <DrawerHead>
          <DrawerLabel>Menu</DrawerLabel>
          <Burger onClick={closeDrawer} aria-label="Close menu">
            <IconClose size={20} />
          </Burger>
        </DrawerHead>

        <DrawerNav aria-label="Mobile">
          {SECTIONS.map((section, index) => (
            <DrawerItem
              key={section.id}
              ref={index === 0 ? firstItemRef : undefined}
              onClick={handleSection(section.id)}
            >
              {section.label}
            </DrawerItem>
          ))}
        </DrawerNav>

        <DrawerFooter>
          <Button variant="primary" block onClick={handleSection("download")}>
            Play free
          </Button>
        </DrawerFooter>
      </DrawerPanel>
    </>
  );
};

export default Header;
